import { Helmet } from '../components/Helmet';
import { Link } from 'react-router-dom';
import { MapPin, CheckCircle, Users, Clock, Star, Building2, ChevronDown, ChevronUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { useState } from 'react';

const faqs = [
  {
    q: "Vous déplacez-vous dans toute l'Île-de-France ?",
    a: "Oui, nous intervenons dans toute l'Île-de-France : Paris intra-muros, La Défense, Hauts-de-Seine, Val-de-Marne, Seine-Saint-Denis, Yvelines, Essonne et au-delà. La formation se déroule dans vos locaux ou dans un espace que vous souhaitez.",
  },
  {
    q: "Combien faut-il de participants minimum pour une formation IA en entreprise à Paris ?",
    a: "Nos ateliers et formations démarrent à partir de 5 participants pour les formats stratégie et atelier métiers. Pour les séminaires et team buildings, nous accueillons de 10 à 200 personnes en une seule session.",
  },
  {
    q: "Proposez-vous des formations IA pour les PME franciliennes ?",
    a: "Absolument. Nous travaillons spécifiquement avec des dirigeants et responsables RH/formation de PME de 30 à 200 personnes. Nos programmes sont pensés pour des structures qui n'ont pas encore de département IA interne.",
  },
  {
    q: "Sous quel délai peut-on organiser une formation IA à Paris ?",
    a: "En général, nous pouvons planifier une intervention sous 2 à 4 semaines après la validation du programme. Pour les séminaires grands formats, prévoir 4 à 6 semaines.",
  },
  {
    q: "Comment se passe la prise en charge OPCO pour une formation IA en Île-de-France ?",
    a: "Nous vous accompagnons dans les démarches de financement OPCO (Constructys, OPCO2i, Atlas…). Nos formations peuvent être financées dans le cadre du plan de développement des compétences.",
  },
];

const zones = [
  "Paris (tous arrondissements)",
  "La Défense / Courbevoie",
  "Boulogne-Billancourt",
  "Neuilly-sur-Seine",
  "Saint-Denis / Plaine Commune",
  "Versailles / Yvelines",
  "Massy / Essonne",
  "Créteil / Val-de-Marne",
];

const highlights = [
  {
    icon: <MapPin className="h-6 w-6 text-accent" />,
    title: "Basés à La Défense",
    desc: "Notre équipe est installée à Coeur Défense, au cœur du premier quartier d'affaires européen.",
  },
  {
    icon: <Building2 className="h-6 w-6 text-accent" />,
    title: "Expérience grands groupes & PME",
    desc: "EDF, RATP, L'Oréal, Thales, Vinci… et des dizaines de PME franciliennes formées depuis 2019.",
  },
  {
    icon: <Users className="h-6 w-6 text-accent" />,
    title: "Formations intra-entreprise",
    desc: "Nous venons directement dans vos locaux. Zéro déplacement pour vos équipes.",
  },
  {
    icon: <Clock className="h-6 w-6 text-accent" />,
    title: "Formats flexibles",
    desc: "2 heures de webinaire à 5 jours de parcours complet. Nous adaptons au contexte de votre organisation.",
  },
];

const formations = [
  {
    title: "Atelier IA Métiers",
    desc: "Un atelier collaboratif pour identifier vos cas d'usage IA prioritaires, par métier et par service.",
    duration: "1 journée",
    public: "Managers & équipes",
    slug: "/DemandeInfo/",
  },
  {
    title: "Team Building IA",
    desc: "Découvrir l'IA par la pratique, en équipe. Concret, engageant et directement applicable.",
    duration: "1 journée",
    public: "Tout public",
    slug: "/DemandeInfo/",
  },
  {
    title: "Séminaire IA Grand Format",
    desc: "Sensibiliser 10 à 200 collaborateurs aux enjeux de l'IA en une demi-journée.",
    duration: "1/2 journée",
    public: "Tout public",
    slug: "/DemandeInfo/",
  },
  {
    title: "Formation IA pour Managers",
    desc: "Comprendre, décider et prioriser les investissements IA. Conçue pour les dirigeants de PME.",
    duration: "1 journée",
    public: "Dirigeants & Managers",
    slug: "/DemandeInfo/",
  },
];

const FormationIAParisPage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Upnov IA",
    "description": "Formations en Intelligence Artificielle pour entreprises à Paris et en Île-de-France",
    "url": "https://www.upnov.com/formation-ia-paris",
    "telephone": "",
    "email": "info at upnov.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Coeur Défense",
      "addressLocality": "Courbevoie",
      "postalCode": "92400",
      "addressRegion": "Île-de-France",
      "addressCountry": "FR"
    },
    "areaServed": {
      "@type": "AdministrativeArea",
      "name": "Île-de-France"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Formations IA en entreprise",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Atelier IA Métiers Paris" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Team Building IA Paris" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Séminaire IA Paris" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Formation IA Managers Île-de-France" } },
      ]
    }
  };

  return (
    <>
      <Helmet>
        <title>Formation IA Paris & Île-de-France</title>
        <meta
          name="description"
          content="Formation IA en entreprise à Paris et en Île-de-France. Ateliers, team building IA, séminaires et parcours sur mesure. Interventions intra-entreprise. Devis gratuit."
        />
        <link rel="canonical" href="https://www.upnov.com/formation-ia-paris" />
        <meta property="og:title" content="Formation IA Paris & Île-de-France" />
        <meta property="og:description" content="Formation IA en entreprise à Paris et en Île-de-France. Ateliers, team building IA, séminaires et parcours sur mesure. Interventions intra-entreprise." />
        <meta property="og:url" content="https://www.upnov.com/formation-ia-paris" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.upnov.com/blogimg/team-building-ia.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-primary text-white py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 text-white text-sm font-medium px-4 py-2 rounded-full mb-6">
              <MapPin className="h-4 w-4" />
              <span>Basés à La Défense — Interventions dans toute l'IDF</span>
            </div>
            <h1 className="heading-lg mb-6">
              Formation IA en entreprise<br />à Paris et en Île-de-France
            </h1>
            <p className="text-xl mb-8 text-white/90">
              Ateliers, team buildings, séminaires et parcours de transformation IA.
              Nous intervenons directement dans vos locaux, partout en Île-de-France.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/DemandeInfo/" className="btn-accent">
                Demander un devis gratuit
              </Link>
              <a href="#formations" className="btn-outline-white">
                Voir nos formations
              </a>
            </div>
            <div className="mt-10 flex flex-wrap justify-center gap-8 text-white/80 text-sm">
              <span className="flex items-center gap-1"><Star className="h-4 w-4 fill-accent text-accent" /> 98% de satisfaction</span>
              <span className="flex items-center gap-1"><Users className="h-4 w-4" /> +1 300 professionnels formés</span>
              <span className="flex items-center gap-1"><Building2 className="h-4 w-4" /> +200 entreprises accompagnées</span>
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi nous */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="heading-md mb-4">Pourquoi choisir Upnov IA à Paris ?</h2>
            <p className="text-gray-600">
              Expert en formation IA depuis 2019, implanté au cœur de La Défense,
              nous accompagnons aussi bien les grands groupes du CAC 40 que les PME
              franciliennes de 30 à 200 personnes.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {highlights.map((h, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="bg-gray-50 rounded-xl p-6"
              >
                <div className="mb-4">{h.icon}</div>
                <h3 className="font-semibold text-lg mb-2">{h.title}</h3>
                <p className="text-gray-600 text-sm">{h.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Nos formations */}
      <section id="formations" className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="heading-md mb-4">Nos formations IA à Paris</h2>
            <p className="text-gray-600">
              Des formats adaptés à chaque besoin : sensibilisation rapide, atelier métier,
              team building ou parcours de transformation complet.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {formations.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="bg-white rounded-xl shadow-sm p-8 flex flex-col justify-between"
              >
                <div>
                  <h3 className="text-xl font-semibold mb-3">{f.title}</h3>
                  <p className="text-gray-600 mb-6">{f.desc}</p>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-6">
                    <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{f.duration}</span>
                    <span className="flex items-center gap-1"><Users className="h-4 w-4" />{f.public}</span>
                    <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />Île-de-France</span>
                  </div>
                </div>
                <Link to={f.slug} className="btn-primary self-start">
                  Demander un devis
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/formation-ia-entreprise/" className="btn-outline">
              Voir toutes nos formations
            </Link>
          </div>
        </div>
      </section>

      {/* Zones d'intervention */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="heading-md mb-4">Zones d'intervention en Île-de-France</h2>
              <p className="text-gray-600 mb-8">
                Nous nous déplaçons dans toute l'Île-de-France pour intervenir directement
                dans vos locaux. Pas de déplacement pour vos équipes, une logistique simplifiée.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {zones.map((zone, i) => (
                  <li key={i} className="flex items-center gap-2 text-gray-700">
                    <CheckCircle className="h-5 w-5 text-primary shrink-0" />
                    {zone}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-gray-500 mt-6">
                Intervention possible hors IDF sur demande (Lyon, Bordeaux, Lille…).
              </p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-6">
                <MapPin className="h-8 w-8 text-primary shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-lg">Notre adresse</p>
                  <p className="text-gray-600">Coeur Défense<br />92400 Courbevoie, France</p>
                </div>
              </div>
              <hr className="my-6" />
              <p className="text-gray-700 font-medium mb-2">Contactez-nous pour organiser votre formation IA à Paris</p>
              <p className="text-gray-500 text-sm mb-6">Réponse sous 24h — Devis gratuit et sans engagement</p>
              <Link to="/contact/" className="btn-primary w-full text-center block">
                Nous contacter
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Logos clients */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <p className="text-center text-gray-500 text-sm font-medium uppercase tracking-wider mb-10">
            Ils nous font confiance en Île-de-France
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-70">
            {["EDF", "RATP", "L'Oreal", "Thales", "Vinci", "SNCF", "La Poste", "Carrefour"].map((name) => (
              <img
                key={name}
                src={`/logo/logo-${name.toLowerCase().replace(/[^a-z]/g, '')}.webp`}
                alt={`${name} - client formation IA Paris`}
                className="h-16 object-contain grayscale"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-white">
        <div className="container-custom max-w-3xl mx-auto">
          <h2 className="heading-md text-center mb-10">Questions fréquentes</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left font-medium hover:bg-gray-50 transition-colors"
                >
                  <span>{faq.q}</span>
                  {openFaq === i
                    ? <ChevronUp className="h-5 w-5 text-primary shrink-0 ml-4" />
                    : <ChevronDown className="h-5 w-5 text-gray-400 shrink-0 ml-4" />
                  }
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-6 text-gray-600">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Aller plus loin */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <h2 className="heading-md text-center mb-10">Aller plus loin avec Upnov</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/formation-ia-entreprise/"
              className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow group"
            >
              <p className="font-semibold text-lg mb-2 group-hover:text-primary transition-colors">Catalogue de formations IA</p>
              <p className="text-gray-600 text-sm">Tous nos programmes : fondamentaux, métiers, managers et transformation IA.</p>
              <span className="text-primary text-sm font-medium mt-4 inline-block">Voir les formations →</span>
            </Link>
            <Link
              to="/accompagnement-ia-entreprise/"
              className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow group"
            >
              <p className="font-semibold text-lg mb-2 group-hover:text-primary transition-colors">Accompagnement IA sur mesure</p>
              <p className="text-gray-600 text-sm">Au-delà de la formation : diagnostic, feuille de route et déploiement opérationnel de l'IA dans votre entreprise.</p>
              <span className="text-primary text-sm font-medium mt-4 inline-block">Découvrir l'accompagnement →</span>
            </Link>
            <Link
              to="/demarrer-votre-diagnostic-ia-entreprise/"
              className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow group"
            >
              <p className="font-semibold text-lg mb-2 group-hover:text-primary transition-colors">Diagnostic IA gratuit</p>
              <p className="text-gray-600 text-sm">Évaluez la maturité IA de votre entreprise en 5 minutes et recevez des recommandations personnalisées.</p>
              <span className="text-primary text-sm font-medium mt-4 inline-block">Démarrer le diagnostic →</span>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="bg-primary text-white py-16">
        <div className="container-custom text-center max-w-2xl mx-auto">
          <h2 className="heading-md mb-4">Organisons votre formation IA à Paris</h2>
          <p className="text-white/90 text-lg mb-8">
            Dites-nous ce que vous souhaitez faire, le nombre de participants et votre
            disponibilité. Nous revenons vers vous sous 24h avec une proposition.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/DemandeInfo/" className="btn-accent">
              Demander un devis gratuit
            </Link>
            <Link to="/contact/" className="btn-outline-white">
              Nous écrire
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default FormationIAParisPage;
